import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { customerTaskQueryOptions } from '@/entities/task'
import { ApiError } from '@/shared/api'
import { formatAbsolute, formatDateTime } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { DueDate } from '@/shared/ui/due-date'
import { Icon } from '@/shared/ui/icon'
import { MarkdownView } from '@/shared/ui/markdown'
import { PriorityChip } from '@/shared/ui/priority-chip'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

export const Route = createFileRoute('/my/tasks_/$taskId')({
  component: CustomerTaskPage,
  head: () => ({ meta: [{ title: 'Задача · STIT Task' }] }),
})

// Карточка задачи глазами заказчика: только состав customer-view — название,
// описание, этап, срок. Ни обсуждения, ни оценок, ни исполнителей, ни кнопок
// правки: их нет ни в ответе бэкенда, ни здесь (00-architecture § 6).
function CustomerTaskPage() {
  const { taskId } = Route.useParams()
  const taskQ = useQuery(customerTaskQueryOptions(taskId))

  if (taskQ.isPending) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        <Spinner label="Загрузка задачи" />
      </div>
    )
  }

  if (taskQ.isError) {
    // Непомеченная задача отвечает 404, а не 403: сам факт её существования
    // клиенту не раскрывается.
    const notFound = taskQ.error instanceof ApiError && taskQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Задача недоступна' : 'Не удалось загрузить задачу'}
        message={
          notFound
            ? 'Возможно, её ещё не открыли вам или она больше не актуальна.'
            : 'Попробуйте обновить страницу.'
        }
      >
        {!notFound && (
          <Button
            variant="secondary"
            onClick={() => {
              void taskQ.refetch()
            }}
          >
            Повторить
          </Button>
        )}
        <Link
          to="/my/tasks"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К моим задачам
        </Link>
      </StatusScreen>
    )
  }

  const task = taskQ.data

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <nav
        aria-label="Хлебные крошки"
        className="mb-6 flex flex-wrap items-center gap-2 text-sm text-text-tertiary"
      >
        <Link to="/my/tasks" className="transition-colors hover:text-text-primary">
          Все задачи
        </Link>
        {task.projectId && task.projectName && (
          <>
            <span aria-hidden>·</span>
            <Link
              to="/my/projects/$projectId"
              params={{ projectId: task.projectId }}
              className="transition-colors hover:text-text-primary"
            >
              {task.projectName}
            </Link>
          </>
        )}
        {task.boardName && (
          <>
            <span aria-hidden>·</span>
            {/* Доска — не подпись, а место: с карточки видно, куда вернуться за
                соседними задачами того же этапа. */}
            {task.boardId ? (
              <Link
                to="/my/boards/$boardId"
                params={{ boardId: task.boardId }}
                className="transition-colors hover:text-text-primary"
              >
                {task.boardName}
              </Link>
            ) : (
              <span>{task.boardName}</span>
            )}
          </>
        )}
      </nav>

      <header className="flex flex-col gap-4">
        <div className="flex min-w-0 items-start gap-3">
          {task.type === 'bug' && (
            <span
              role="img"
              aria-label="Баг"
              title="Баг"
              className="mt-1 inline-flex shrink-0 text-text-tertiary"
            >
              <Icon name="bug" size={24} />
            </span>
          )}
          <h1 className="t-h2 min-w-0 flex-1 break-words text-text-primary">{task.title}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {task.columnName && (
            <Chip tone={task.done ? 'success' : 'neutral'}>{task.columnName}</Chip>
          )}
          {task.priority !== null && <PriorityChip priority={task.priority} />}
          {/* Закрытая работа не бывает просроченной — тон срока это не скрывает. */}
          {task.dueDate && <DueDate dueDate={task.dueDate} muted={task.done} />}
        </div>
      </header>

      {task.description && (
        <div className="mt-8">
          <MarkdownView value={task.description} className="max-w-[72ch]" />
        </div>
      )}

      {task.customerVisibleAt && (
        <p className="mt-10 text-xs text-text-tertiary">
          Открыта вам{' '}
          <time
            dateTime={task.customerVisibleAt}
            title={formatAbsolute(task.customerVisibleAt, true)}
          >
            {formatDateTime(task.customerVisibleAt)}
          </time>
        </p>
      )}
    </div>
  )
}
